import { DeleteIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Checkbox,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  Textarea,
  useDisclosure,
  useToast,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FaEllipsisV } from "react-icons/fa";
import React from "react";
import useApi from "../../../shared/hooks/useApi";
import {
  NursingQuestion,
  NursingQuestionEditParams,
  NursingQuestionsSettingsResponse,
} from "../nursing-question-settings.consts";
import { emitRefreshPatientQuestionTypes } from "../nursing-question-settings.utils";

export function useUpdateNursingQuestion(question: NursingQuestion) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const listKey = queries.nursingQuestionSettings.list().queryKey;

  return useMutation({
    ...mutations.nursingQuestionSettings.update(question.id),
    onMutate: async (body: NursingQuestionEditParams) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<NursingQuestionsSettingsResponse>(listKey);
      queryClient.setQueryData<NursingQuestionsSettingsResponse>(listKey, (data) =>
        data === undefined
          ? data
          : {
              questions: data.questions.map((q) => (q.id === question.id ? { ...q, ...body } : q)),
            }
      );
      return { previous };
    },
    onError: (_error, _body, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(listKey, context.previous);
      }
      toast({
        title: "Failed updating question type",
        status: "error",
        position: "top-right",
      });
    },
    onSuccess: () => {
      toast({
        title: "Question type updated successfully",
        status: "success",
        position: "top-right",
      });
      emitRefreshPatientQuestionTypes();
    },
  });
}

export function QuestionTextCell(props: { question: NursingQuestion }) {
  const update = useUpdateNursingQuestion(props.question);
  const [value, setValue] = React.useState(props.question.questionText);

  React.useEffect(() => {
    setValue(props.question.questionText);
  }, [props.question.questionText]);

  const handleBlur = () => {
    if (value.trim() === "") {
      setValue(props.question.questionText);
      return;
    }
    if (value !== props.question.questionText) {
      update.mutate({ questionText: value });
    }
  };

  return (
    <Textarea
      minW="72"
      rows={2}
      size="sm"
      value={value}
      onBlur={handleBlur}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

export function QuestionToggleCell(props: {
  field: "isVBP" | "showOnPatient" | "showOnAgency" | "transferAnswerBetweenTasks";
  isDisabled: boolean;
  question: NursingQuestion;
}) {
  const update = useUpdateNursingQuestion(props.question);

  return (
    <Checkbox
      colorScheme="green"
      isChecked={props.question[props.field]}
      isDisabled={props.isDisabled}
      size="lg"
      onChange={(e) => update.mutate({ [props.field]: e.target.checked })}
    />
  );
}

export function PossibleAnswersCell(props: { question: NursingQuestion }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const answers = props.question.possibleAnswers ?? [];

  if (answers.length === 0) {
    return null;
  }

  const visibleAnswers = isExpanded ? answers : answers.slice(0, 3);

  return (
    <Wrap maxW="80" spacing={1}>
      {visibleAnswers.map((answer, index) => (
        <WrapItem key={index}>
          <Tag size="sm">{answer.text}</Tag>
        </WrapItem>
      ))}
      {answers.length > 3 && (
        <WrapItem>
          <Button size="xs" variant="link" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? "Hide" : `${answers.length - 3} More`}
          </Button>
        </WrapItem>
      )}
    </Wrap>
  );
}

export function QuestionActionsCell(props: {
  question: NursingQuestion;
  onEditAlerts: (question: NursingQuestion) => void;
}) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const deleteDisclosure = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const listKey = queries.nursingQuestionSettings.list().queryKey;

  const removeMutation = useMutation({
    ...mutations.nursingQuestionSettings.remove(props.question.id),
    onError: () => {
      toast({
        title: "Failed deleting question type",
        status: "error",
        position: "top-right",
      });
    },
    onSuccess: () => {
      queryClient.setQueryData<NursingQuestionsSettingsResponse>(listKey, (data) =>
        data === undefined
          ? data
          : { questions: data.questions.filter((q) => q.id !== props.question.id) }
      );
      toast({
        title: "Question type deleted successfully",
        status: "success",
        position: "top-right",
      });
      emitRefreshPatientQuestionTypes();
      deleteDisclosure.onClose();
    },
  });

  const canEditAlerts = props.question.isVBP && (props.question.possibleAnswers ?? []).length > 0;

  return (
    <>
      <Menu>
        <MenuButton
          aria-label="Question actions"
          as={IconButton}
          icon={<FaEllipsisV />}
          variant="ghost"
        />
        <MenuList>
          <MenuItem color="red.500" icon={<DeleteIcon />} onClick={deleteDisclosure.onOpen}>
            Remove Question
          </MenuItem>
          {canEditAlerts && (
            <MenuItem onClick={() => props.onEditAlerts(props.question)}>Edit alerts</MenuItem>
          )}
        </MenuList>
      </Menu>

      <AlertDialog
        isOpen={deleteDisclosure.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDisclosure.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete?</AlertDialogHeader>
            <AlertDialogBody>Are you sure you want to delete this question?</AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button ref={cancelRef} variant="ghost" onClick={deleteDisclosure.onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                isLoading={removeMutation.isPending}
                onClick={() => removeMutation.mutate()}
              >
                Yes, delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
